import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Input, Alert, Progress, message } from 'antd';
import { EditOutlined, TranslationOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { TextArea } = Input;

const isFilled = (v) => v != null && String(v).trim() !== '';

// 英文字段：[英文字段, 中文源字段, 显示名]
const EN_FIELDS = [
  ['titleEn', 'title', '标题'],
  ['routeEn', 'route', '交通'],
  ['descriptionEn', 'description', '行程描述'],
  ['accommodationEn', 'accommodation', '住宿'],
];

/** 出境游英文行程单（过海关用）：按产品每日行程逐天翻译、校对 */
const ItineraryEnCard = ({ destinationId }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } | null
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchDays = useCallback(async () => {
    if (!destinationId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/product-days/destination/${destinationId}`, { headers: AuthService.getAuthHeaders() });
      setDays(res.data?.data || []);
    } catch (err) {
      message.error('获取每日行程失败');
    } finally {
      setLoading(false);
    }
  }, [destinationId]);

  useEffect(() => { fetchDays(); }, [fetchDays]);

  const missing = days.filter((d) => !isFilled(d.titleEn));

  // 逐天请求，单个请求只翻一天，避免长请求超时
  const translateDays = async (targets, overwrite) => {
    if (!targets.length || progress) return;
    setProgress({ done: 0, total: targets.length });
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        await axios.post(`${API_BASE}/product-days/${targets[i].id}/translate`, { overwrite }, { headers: AuthService.getAuthHeaders() });
      } catch (err) {
        failed += 1;
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setProgress(null);
    await fetchDays();
    if (failed) message.error(`${failed} 天翻译失败，请重试`);
    else message.success(`已翻译 ${targets.length} 天`);
  };

  const confirmRetranslateAll = () => {
    Modal.confirm({
      title: '全部重新翻译',
      content: '将覆盖已有英文内容，包括人工修改过的部分。',
      okText: '重新翻译',
      cancelText: '取消',
      onOk: () => translateDays(days, true),
    });
  };

  const openEdit = (day) => {
    setEditing(day);
    setForm(Object.fromEntries(EN_FIELDS.map(([en]) => [en, day[en] || ''])));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = Object.fromEntries(EN_FIELDS.map(([en]) => [en, form[en]?.trim() ? form[en] : null]));
      await axios.put(`${API_BASE}/product-days/${editing.id}`, payload, { headers: AuthService.getAuthHeaders() });
      message.success('已保存');
      setEditing(null);
      fetchDays();
    } catch (err) {
      message.error('保存失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Day', dataIndex: 'dayNumber', key: 'day', width: 64, render: (v) => `Day ${v}` },
    { title: '中文标题', dataIndex: 'title', key: 'title', width: 260, ellipsis: true },
    {
      title: 'English',
      dataIndex: 'titleEn',
      key: 'titleEn',
      ellipsis: true,
      render: (v) => (isFilled(v) ? v : <Tag>未翻译</Tag>),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, r) => (
        <Space size={8}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Button size="small" icon={<TranslationOutlined />} disabled={!!progress} onClick={() => translateDays([r], true)}>翻译</Button>
        </Space>
      ),
    },
  ];

  if (!destinationId) return null;

  return (
    <Card
      title="英文行程单"
      size="small"
      style={{ marginTop: 16 }}
      extra={(
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchDays} disabled={!!progress}>刷新</Button>
          {days.length > 0 && (
            <Button size="small" onClick={confirmRetranslateAll} disabled={!!progress}>全部重新翻译</Button>
          )}
          {missing.length > 0 && (
            <Button size="small" type="primary" icon={<TranslationOutlined />} loading={!!progress} onClick={() => translateDays(missing, false)}>
              翻译未翻译的 {missing.length} 天
            </Button>
          )}
        </Space>
      )}
    >
      {progress && (
        <Progress percent={Math.round((progress.done / progress.total) * 100)} format={() => `${progress.done}/${progress.total}`} style={{ marginBottom: 12 }} />
      )}
      {!loading && days.length === 0 && (
        <Alert type="warning" showIcon message="该产品未录入每日行程，无法生成英文行程单" style={{ marginBottom: 12 }} />
      )}
      {days.length > 0 && (
        <Alert
          type={missing.length ? 'warning' : 'success'}
          showIcon
          message={missing.length ? `${missing.length} 天未翻译，客人端暂不显示英文行程单` : `英文行程单已就绪，共 ${days.length} 天`}
          style={{ marginBottom: 12 }}
        />
      )}
      {days.length > 0 && (
        <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={days} pagination={false} />
      )}

      <Modal
        title={editing ? `Day ${editing.dayNumber} 英文内容` : ''}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        width={860}
        okText="保存"
        cancelText="取消"
        maskClosable={false}
      >
        {editing && EN_FIELDS.map(([en, cn, label]) => (
          <div key={en} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
            {isFilled(editing[cn]) && (
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4, whiteSpace: 'pre-wrap' }}>{editing[cn]}</div>
            )}
            {en === 'descriptionEn' ? (
              <TextArea rows={5} value={form[en]} onChange={(e) => setForm((f) => ({ ...f, [en]: e.target.value }))} />
            ) : (
              <Input value={form[en]} onChange={(e) => setForm((f) => ({ ...f, [en]: e.target.value }))} />
            )}
          </div>
        ))}
      </Modal>
    </Card>
  );
};

export default ItineraryEnCard;
